import { useCallback, useEffect, useRef, useState } from "react";
import flatpickr from "flatpickr";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ChevronDown, Plus, Trash2 } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import DatePicker from "../../../../shared/ui/form/date-picker";
import Input from "../../../../shared/ui/form/input/InputField";
import NotionEditor from "../../../../shared/ui/form/input/NotionEditor";
import Label from "../../../../shared/ui/form/Label";
import { Modal } from "../../../../shared/ui/modal";
import { useCreateCourse, useUpdateCourse, COURSE_STATUSES, CenterCourse, CoursePhase, CourseStatus } from "../../../center";
import { COURSE_ERRORS } from "../refusals";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    centerId: string;
    /** The course being edited; a new one when absent. */
    course?: CenterCourse | null;
    /** A course to start a new one from: its plan, under names marked as a copy. */
    copyOf?: CenterCourse | null;
}

/** A phase as the form holds it: topics as the text typed. */
interface PhaseDraft {
    key: number;
    /** The saved module's id, so an edit keeps it; none for a new one. */
    id?: string;
    name: string;
    description: string;
    starts_on: string;
    ends_on: string;
    topics: string;
    summary: string;
}

const SELECT_CLASS =
    "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm dark:border-gray-700";
const TEXTAREA_CLASS = "field-base field-default w-full px-4 py-2.5 text-sm";

/**
 * The app's date picker for one date of a module.
 *
 * The picker rebuilds itself whenever its callback or its default date
 * changes, so both are held steady here: the default is the value it opened
 * with, and the callback reads the latest handler through a ref.
 */
function ModuleDateField({
    id,
    value,
    placeholder,
    onPick,
}: {
    id: string;
    value: string;
    placeholder: string;
    onPick: (value: string) => void;
}) {
    const [initial] = useState(value || undefined);
    const latest = useRef(onPick);
    useEffect(() => {
        latest.current = onPick;
    });
    const handle = useCallback(
        (dates: Date[]) => latest.current(dates[0] ? flatpickr.formatDate(dates[0], "Y-m-d") : ""),
        []
    );
    return <DatePicker id={id} defaultDate={initial} onChange={handle} placeholder={placeholder} />;
}

let nextKey = 0;
const blankPhase = (): PhaseDraft => ({
    key: nextKey++,
    name: "",
    description: "",
    starts_on: "",
    ends_on: "",
    topics: "",
    summary: "",
});

const toDraft = (phase: CoursePhase): PhaseDraft => ({
    key: nextKey++,
    id: phase.id,
    name: phase.name,
    description: phase.description ?? "",
    starts_on: phase.starts_on ?? "",
    ends_on: phase.ends_on ?? "",
    topics: phase.topics.join(", "),
    summary: phase.summary ?? "",
});

const toPhase = (draft: PhaseDraft): CoursePhase => ({
    ...(draft.id ? { id: draft.id } : {}),
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    starts_on: draft.starts_on || null,
    ends_on: draft.ends_on || null,
    topics: draft.topics
        .split(",")
        .map((topic) => topic.trim())
        .filter(Boolean),
    summary: draft.summary.trim() || null,
});

/**
 * Creating or editing a course, with its pedagogical plan.
 *
 * The public name is what its students read; the internal one is how the
 * staff tell intakes apart. The plan is its phases (or modules), in the order
 * they are taught; the course runs from the first phase's start to the last
 * one's end.
 */
export default function CourseFormModal({ isOpen, onClose, centerId, course, copyOf }: Props) {
    const { t } = useTranslation();
    const create = useCreateCourse(centerId);
    const update = useUpdateCourse(centerId);
    const editing = Boolean(course);

    const [name, setName] = useState("");
    const [internalName, setInternalName] = useState("");
    const [status, setStatus] = useState<CourseStatus>("planned");
    const [phases, setPhases] = useState<PhaseDraft[]>([]);
    // Which modules are open, by key. A saved plan opens folded, so the
    // course reads as its list of modules; a new one opens its blank module.
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    // The module just added, so it can be brought into view and named.
    const [focusKey, setFocusKey] = useState<number | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const copySuffix = ` - ${t("center.courses.copySuffix")}`;

    useEffect(() => {
        if (!isOpen) return;
        const source = course ?? copyOf;
        setName(course?.name ?? (copyOf ? copyOf.name + copySuffix : ""));
        setInternalName(course?.internal_name ?? (copyOf ? copyOf.internal_name + copySuffix : ""));
        // A copy is a new intake: it starts planned, whatever its source has reached.
        setStatus(course?.status ?? "planned");
        // A copy's modules are new ones: without their ids, nothing taught
        // in the source's modules is counted against the copy's.
        const drafts = source?.phases.length
            ? source.phases.map((phase) => toDraft(course ? phase : { ...phase, id: undefined }))
            : [blankPhase()];
        setPhases(drafts);
        setExpanded(new Set(source?.phases.length ? [] : drafts.map((draft) => draft.key)));
        setFocusKey(null);
        setSubmitted(false);
        setError(null);
    }, [isOpen, course, copyOf, copySuffix]);

    const changePhase = (key: number, changes: Partial<PhaseDraft>) =>
        setPhases((current) => current.map((phase) => (phase.key === key ? { ...phase, ...changes } : phase)));

    const movePhase = (index: number, by: number) =>
        setPhases((current) => {
            const next = [...current];
            const [moved] = next.splice(index, 1);
            next.splice(index + by, 0, moved);
            return next;
        });

    const toggleExpanded = (key: number) =>
        setExpanded((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    const allExpanded = phases.length > 0 && phases.every((phase) => expanded.has(phase.key));

    // Adding folds the others away, so the new module is not buried under
    // the ones already written; it opens, scrolls into view and takes focus.
    const addPhase = () => {
        const phase = blankPhase();
        setPhases((current) => [...current, phase]);
        setExpanded(new Set([phase.key]));
        setFocusKey(phase.key);
    };

    useEffect(() => {
        if (focusKey === null) return;
        const field = document.getElementById(`phase-${focusKey}-name`);
        field?.scrollIntoView?.({ block: "center", behavior: "smooth" });
        field?.focus({ preventScroll: true });
        setFocusKey(null);
    }, [focusKey]);

    const phaseDatesInvalid = (phase: PhaseDraft) =>
        Boolean(phase.starts_on && phase.ends_on && phase.ends_on < phase.starts_on);
    const invalid =
        !name.trim() ||
        !internalName.trim() ||
        phases.some((phase) => !phase.name.trim() || phaseDatesInvalid(phase));

    const handleSubmit = async () => {
        // Said on the click, beside each field, rather than by greying the
        // button out: a disabled button does not say what it is waiting for.
        setSubmitted(true);
        setError(null);
        if (invalid) {
            // A folded module would hide the very field that is wrong.
            setExpanded(
                (current) =>
                    new Set([
                        ...current,
                        ...phases
                            .filter((phase) => !phase.name.trim() || phaseDatesInvalid(phase))
                            .map((phase) => phase.key),
                    ])
            );
            return;
        }
        const input = {
            name: name.trim(),
            internal_name: internalName.trim(),
            status,
            phases: phases.map(toPhase),
        };
        try {
            if (course) {
                await update.mutateAsync({ courseId: course.id, changes: input });
            } else {
                await create.mutateAsync(input);
            }
            onClose();
        } catch (err: unknown) {
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
            setError(t((detail && COURSE_ERRORS[detail]) || "center.courses.errorGeneric"));
        }
    };

    const pending = create.isPending || update.isPending;
    const title = editing
        ? t("center.courses.editTitle")
        : copyOf
          ? t("center.courses.copyTitle")
          : t("center.courses.newTitle");

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel={title}
            className="mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-surface-raised p-6"
        >
            <form
                className="flex flex-col gap-5"
                noValidate
                onSubmit={(e) => {
                    e.preventDefault();
                    void handleSubmit();
                }}
            >
                <div>
                    <h3 className="text-lg font-semibold text-content-primary">{title}</h3>
                    <p className="mt-1 text-sm text-content-tertiary">{t("center.courses.formSubtitle")}</p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                        <Label htmlFor="course-public-name">{t("center.courses.publicName")}</Label>
                        <Input
                            id="course-public-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t("center.courses.namePlaceholder")}
                            error={submitted && !name.trim()}
                            aria-invalid={submitted && !name.trim()}
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {t("center.courses.publicNameHint")}
                        </p>
                    </div>
                    <div>
                        <Label htmlFor="course-internal-name">{t("center.courses.internalName")}</Label>
                        <Input
                            id="course-internal-name"
                            value={internalName}
                            onChange={(e) => setInternalName(e.target.value)}
                            placeholder={t("center.courses.internalNamePlaceholder")}
                            error={submitted && !internalName.trim()}
                            aria-invalid={submitted && !internalName.trim()}
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {t("center.courses.internalNameHint")}
                        </p>
                    </div>
                    <div>
                        <Label htmlFor="course-status">{t("center.courses.status")}</Label>
                        <select
                            id="course-status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value as CourseStatus)}
                            className={SELECT_CLASS}
                        >
                            {COURSE_STATUSES.map((option) => (
                                <option key={option} value={option}>
                                    {t(`center.courses.statuses.${option}`)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <fieldset className="flex flex-col gap-3">
                    <legend className="mb-1 flex w-full items-center justify-between gap-2 text-sm font-semibold text-content-primary">
                        {t("center.courses.plan.title")}
                        {phases.length > 1 && (
                            <button
                                type="button"
                                onClick={() =>
                                    setExpanded(allExpanded ? new Set() : new Set(phases.map((phase) => phase.key)))
                                }
                                className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-300"
                            >
                                {allExpanded
                                    ? t("center.courses.plan.collapseAll")
                                    : t("center.courses.plan.expandAll")}
                            </button>
                        )}
                    </legend>
                    <p className="-mt-1 text-xs text-gray-500 dark:text-gray-400">{t("center.courses.plan.hint")}</p>

                    {phases.map((phase, index) => {
                        const nameMissing = submitted && !phase.name.trim();
                        const datesInvalid = phaseDatesInvalid(phase);
                        const id = (field: string) => `phase-${phase.key}-${field}`;
                        const isOpen = expanded.has(phase.key);
                        const hasError = nameMissing || datesInvalid;
                        const dates = [phase.starts_on, phase.ends_on].filter(Boolean).join(" → ");
                        return (
                            <div
                                key={phase.key}
                                role="group"
                                aria-label={t("center.courses.plan.phaseN", { n: index + 1 })}
                                className="rounded-xl border border-gray-200 p-4 dark:border-gray-800"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <button
                                        type="button"
                                        onClick={() => toggleExpanded(phase.key)}
                                        aria-expanded={isOpen}
                                        aria-controls={id("body")}
                                        className="flex min-w-0 flex-1 items-center gap-2 rounded-lg py-1 text-left"
                                    >
                                        <ChevronDown
                                            size={16}
                                            aria-hidden="true"
                                            className={`shrink-0 text-content-tertiary transition-transform ${isOpen ? "" : "-rotate-90"}`}
                                        />
                                        <span className="flex min-w-0 flex-col">
                                            <span className="text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-300">
                                                {t("center.courses.plan.phaseN", { n: index + 1 })}
                                            </span>
                                            {!isOpen && (
                                                <span className="truncate text-sm font-medium text-content-primary">
                                                    {phase.name.trim() || (
                                                        <span className="italic text-content-tertiary">
                                                            {t("center.courses.plan.untitled")}
                                                        </span>
                                                    )}
                                                    {dates && (
                                                        <span className="ml-2 text-xs font-normal text-content-tertiary">
                                                            {dates}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                        {!isOpen && hasError && (
                                            <span className="ml-auto shrink-0 text-xs font-medium text-red-600 dark:text-red-400">
                                                {t("center.courses.plan.needsAttention")}
                                            </span>
                                        )}
                                    </button>
                                    <div className="flex shrink-0 gap-1">
                                        <button
                                            type="button"
                                            onClick={() => movePhase(index, -1)}
                                            disabled={index === 0}
                                            aria-label={t("center.courses.plan.moveUp", { n: index + 1 })}
                                            className="inline-flex size-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-white/5"
                                        >
                                            <ArrowUp size={15} aria-hidden="true" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => movePhase(index, 1)}
                                            disabled={index === phases.length - 1}
                                            aria-label={t("center.courses.plan.moveDown", { n: index + 1 })}
                                            className="inline-flex size-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-white/5"
                                        >
                                            <ArrowDown size={15} aria-hidden="true" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setPhases((current) => current.filter((p) => p.key !== phase.key))
                                            }
                                            aria-label={t("center.courses.plan.remove", { n: index + 1 })}
                                            className="inline-flex size-9 items-center justify-center rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"
                                        >
                                            <Trash2 size={15} aria-hidden="true" />
                                        </button>
                                    </div>
                                </div>
                                {/* Hidden, not unmounted: the date pickers and the
                                    summary editor keep their state while folded. */}
                                <div
                                    id={id("body")}
                                    hidden={!isOpen}
                                    className={`mt-3 gap-3 sm:grid-cols-2 ${isOpen ? "grid" : "hidden"}`}
                                >
                                    <div className="sm:col-span-2">
                                        <Label htmlFor={id("name")}>{t("center.courses.plan.name")}</Label>
                                        <Input
                                            id={id("name")}
                                            value={phase.name}
                                            onChange={(e) => changePhase(phase.key, { name: e.target.value })}
                                            placeholder={t("center.courses.plan.namePlaceholder")}
                                            error={nameMissing}
                                            aria-invalid={nameMissing}
                                        />
                                        {nameMissing && (
                                            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                                {t("center.courses.plan.errorName")}
                                            </p>
                                        )}
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Label htmlFor={id("description")}>{t("center.courses.plan.description")}</Label>
                                        <textarea
                                            id={id("description")}
                                            rows={2}
                                            value={phase.description}
                                            onChange={(e) => changePhase(phase.key, { description: e.target.value })}
                                            className={TEXTAREA_CLASS}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor={id("starts")}>{t("center.courses.startsOn")}</Label>
                                        <ModuleDateField
                                            id={id("starts")}
                                            value={phase.starts_on}
                                            placeholder={t("session.selectStartDate")}
                                            onPick={(value) => changePhase(phase.key, { starts_on: value })}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor={id("ends")}>{t("center.courses.endsOn")}</Label>
                                        <ModuleDateField
                                            id={id("ends")}
                                            value={phase.ends_on}
                                            placeholder={t("session.selectEndDate")}
                                            onPick={(value) => changePhase(phase.key, { ends_on: value })}
                                        />
                                    </div>
                                    {datesInvalid && (
                                        <p className="text-xs text-red-600 dark:text-red-400 sm:col-span-2">
                                            {t("center.courses.plan.errorDates")}
                                        </p>
                                    )}
                                    <div className="sm:col-span-2">
                                        <Label htmlFor={id("topics")}>{t("center.courses.plan.topics")}</Label>
                                        <Input
                                            id={id("topics")}
                                            value={phase.topics}
                                            onChange={(e) => changePhase(phase.key, { topics: e.target.value })}
                                            placeholder={t("center.courses.plan.topicsPlaceholder")}
                                        />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <p id={id("summary-label")} className="mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-400">
                                            {t("center.courses.plan.summary")}
                                        </p>
                                        {/* The same editor as session notes: headings, lists,
                                            links — room for what is worth knowing about the module. */}
                                        <div role="group" aria-labelledby={id("summary-label")}>
                                            <NotionEditor
                                                value={phase.summary}
                                                onChange={(value) => changePhase(phase.key, { summary: value })}
                                                placeholder={t("center.courses.plan.summaryPlaceholder")}
                                                className="min-h-[140px]"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    <div>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={addPhase}
                            startIcon={<Plus size={14} aria-hidden="true" />}
                        >
                            {t("center.courses.plan.add")}
                        </Button>
                    </div>
                </fieldset>

                {submitted && invalid && (
                    <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                        {t("center.courses.errorForm")}
                    </p>
                )}
                {error && (
                    <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                        {error}
                    </p>
                )}

                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>
                        {t("common.cancel")}
                    </Button>
                    <Button type="submit" loading={pending} disabled={pending}>
                        {editing ? t("common.save") : t("center.courses.create")}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
